import React, { useMemo } from "react";
import { AxiosError } from "axios";
import {
  QueryResult,
  QueryKey,
  queryCache,
  useMutation,
  useQuery,
} from "react-query";
import { useTranslation } from "react-i18next";
import format from "date-fns/format";

import { ApiError } from "fe-shared/src/types/common";
import {
  CollapseComponent,
  EntityNavigation,
  LinkButton,
  Loader,
  NotFoundPage,
  RecordsSidebar,
  showNotification,
} from "fe-shared/src/components";
import {
  ArrowRight,
  CloseIcon,
  FlagIcon,
  OutlinedInfoIcon,
  ReturnIcon,
  WarningIcon,
} from "fe-shared/src/components/Icons";
import useRecycleMaterials from "fe-shared/src/hooks/useRecycleMaterials";
import useTenantOptions from "fe-shared/src/hooks/useTenantOptions";

import { PickupRequestDetails as PickupRequestDetailsModel } from "../../types";
import { formatRequestNumber, getFormattedDateRange } from "../../helpers/format";
import { formatWeight, getTotalWeight } from "../../helpers/materials";
import { getCreatedByLabel } from "../../helpers/createdBy";
import { FLAG_COLOR, MATERIAL_ROLES } from "../../consts";
import {
  deleteDocument,
  getDocuments,
  getPickupRequestReasons,
} from "../../api/pickupRequests";
import { addRequestNote, getRequestNotes } from "../../api/requestNotes";

import RequestReasonPriority from "../RequestReasonPriority";
import RequestCompanyBlock from "../RequestCompanyBlock";
import RequestHaulerBlock from "../RequestHaulerBlock";
import RequestDataRow from "../RequestDataRow";
import DialogHeader from "../DialogHeader";
import RequestDocumentThumbnail from "../RequestDocumentThumbnail";

import styles from "./PickupRequestDetails.module.scss";

export interface Props {
  returnUrl: string;
  requestNumber: string;
  request: QueryResult<PickupRequestDetailsModel, AxiosError<ApiError>>;
  navigation?: {
    numbers: string[];
    total: number;
    onNeedMore?: () => void;
  };
  currentUserEmail: string;
  canViewDocuments: boolean;
  /** Priority is portal-only and appears on no participant screen. */
  canViewPriority: boolean;
  /** The flag indicator. Participants receive isFlagged only when the portal set ShowFlagToParticipants,
   *  so the server decides what they see. */
  canViewFlag: boolean;
  /** Carrier assignment is portal/hauler business; a collector-raised request must not show it. */
  canViewCarrier: boolean;
  /** Request reasons are a portal-only intake field; a collector never sets or sees them. */
  canViewReasons: boolean;
  canManageDocuments: boolean;
  canViewHistory: boolean;
  canExportHistory: boolean;
  canViewNotes: boolean;
  canAddNote?: boolean;
  /** Status badge, request number and the assign control - portal-only, external users may not assign. */
  assigneeSlot: React.ReactNode;
  /** Rendered directly under the page title - portal-only, e.g. the linked-transaction link. */
  titleSlot?: React.ReactNode;
  /** Edit trigger and its dialog - portal-only, rendered on the submitted-date header line. */
  headerActionsSlot?: React.ReactNode;
  /** Approve/invalidate controls - portal-only. */
  actionsSlot?: React.ReactNode;
  /** Flag control - portal-only, and shown only where canViewFlag already allows the indicator. */
  flagSlot?: React.ReactNode;
  /** Edit trigger inside the flag banner - portal-only, rendered only while flagged. */
  flagEditSlot?: React.ReactNode;
  /** Edit trigger for the hauler and carrier assignment, rendered beside the hauler block. */
  assignmentActionSlot?: React.ReactNode;
  /** Portal-only: turns the participant number into a link to the participant profile. */
  participantLinkTo?: (companyId: number) => string;
}

const PickupRequestDetails = (props: Props) => {
  const {
    returnUrl,
    requestNumber,
    request,
    navigation,
    currentUserEmail,
    canViewDocuments,
    canViewPriority,
    canViewFlag,
    canViewCarrier,
    canViewReasons,
    canManageDocuments,
    canViewHistory,
    canExportHistory,
    participantLinkTo,
    canViewNotes,
    canAddNote = false,
    assigneeSlot,
    titleSlot,
    headerActionsSlot,
    actionsSlot,
    flagSlot,
    flagEditSlot,
    assignmentActionSlot,
  } = props;
  const { data, isLoading, isError } = request;
  const { t } = useTranslation();
  const [tenantOptions] = useTenantOptions();

  const documentsQueryKey: QueryKey = ["pickupRequestDocuments", requestNumber];
  const documentsQuery = useQuery(
    documentsQueryKey,
    () => getDocuments(requestNumber),
    { enabled: !!data && canViewDocuments }
  );

  const [removeDocument, removeDocumentResult] = useMutation(
    (documentId: number) => deleteDocument(requestNumber, documentId),
    {
      onSuccess: () => queryCache.invalidateQueries(documentsQueryKey),
      onError: () => {
        showNotification(
          t("pickupRequests.details.documentDeleteError"),
          undefined,
          CloseIcon
        );
      },
    }
  );
  const reasonsQuery = useQuery(
    "pickupRequestReasons",
    getPickupRequestReasons,
    { enabled: !!data }
  );

  const { data: recycleMaterials } = useRecycleMaterials(MATERIAL_ROLES);
  const standardWeightByCode = useMemo(
    () =>
      (recycleMaterials ?? []).reduce<Record<string, number>>(
        (result, { code, standardWeight }) => {
          if (!(code in result)) {
            result[code] = standardWeight;
          }

          return result;
        },
        {}
      ),
    [recycleMaterials]
  );

  const isRequestNotFound = request.error?.response?.status === 404;

  const documents = documentsQuery.data ?? [];

  const hasSidebar = canViewHistory || canViewNotes;

  const renderContent = () => {
    const {
      priority,
      priorityReason,
      reasonIds,
      otherReasonText,
      source,
      target,
      targetCompanyId,
      carrierFirstName,
      carrierLastName,
      createdByFullName,
      isCreatedByInternal,
      comment,
      invalidationComment,
      isFlagged,
      flagComment,
      differsFromLinkedTransaction,
      materials,
      pickupDateFrom,
      pickupDateTo,
      submittedDate,
    } = data!;

    const activitiesQueryKey: QueryKey = ["requestsActivities", requestNumber];

    const totalQuantity = materials.reduce(
      (sum, material) => sum + material.quantity,
      0
    );
    const totalWeight = getTotalWeight(materials, standardWeightByCode);
    const carrierName = [carrierFirstName, carrierLastName]
      .filter(Boolean)
      .join(" ");

    const renderDocuments = () => {
      if (!canViewDocuments) {
        return (
          <span className={styles.empty}>
            {t("pickupRequests.details.documentsNoPermission")}
          </span>
        );
      }

      if (documentsQuery.isError) {
        return (
          <span className={styles.empty}>
            {t("pickupRequests.details.documentsLoadError")}
          </span>
        );
      }

      if (!documents.length) {
        return null;
      }

      return (
        <>
          <div className={styles.documentsGrid}>
            {documents.map((doc) => (
              <RequestDocumentThumbnail
                key={doc.documentId}
                requestNumber={requestNumber}
                document={doc}
                onDelete={canManageDocuments ? removeDocument : undefined}
                isDeleting={removeDocumentResult.isLoading}
              />
            ))}
          </div>
          {documents
            .filter((doc) => doc.description)
            .map((doc) => (
              <span key={doc.documentId}>{doc.description}</span>
            ))}
        </>
      );
    };

    return (
      <>
        <main className={styles.reqDetailsContent}>
          <div className={styles.reqDetailsContentWrapper}>
            <div className={styles.reqDetailsNavigation}>
              <LinkButton
                to={returnUrl}
                variant="inline"
                id="request-back-link"
                iconComponent={ReturnIcon}
                className={styles.reqDetailsBackLink}
              >
                {t("request")}
              </LinkButton>
              {navigation && (
                <EntityNavigation
                  idPrefix="request"
                  numbers={navigation.numbers}
                  total={navigation.total}
                  currentNumber={requestNumber}
                  buildUrl={(nextNumber) =>
                    `${returnUrl}/${nextNumber}?navigation=true`}
                  formatPosition={(position, total) =>
                    t("pickupRequests.navigation.position", { position, total })}
                  previousLabel={t("pickupRequests.navigation.previous")}
                  nextLabel={t("pickupRequests.navigation.next")}
                  onNeedMore={navigation.onNeedMore}
                />
              )}
            </div>

            <div className={styles.reqDetailsHeader}>
              {assigneeSlot}
              <div className={styles.reqDetailsHeaderMain}>
                <div className={styles.titleBlock}>
                  <h1 className={styles.pageTitle}>
                    {t("pickupRequests.createDialog.title")}
                  </h1>
                  {titleSlot}
                </div>
                <div className={styles.headerField}>
                  <span className={styles.headerLabel}>
                    {t("pickupRequests.grid.cols.createdBy")}
                  </span>
                  <span>
                    {getCreatedByLabel(
                      t,
                      tenantOptions.tenantName,
                      createdByFullName,
                      isCreatedByInternal
                    ) || "-"}
                  </span>
                </div>
                <div className={styles.headerField}>
                  <span className={styles.headerLabel}>
                    {t("pickupRequests.grid.cols.submittedOn")}
                  </span>
                  <span>{`${format(new Date(submittedDate), "MMM d, yyyy")},`}</span>
                  <span className={styles.headerTime}>
                    {format(new Date(submittedDate), "h:mm a")}
                  </span>
                </div>
                {/* Its auto left margin is half of what centres the created-by/submitted-on pair.
                    Rendering it empty on a participant surface would hold the pair off the right
                    edge with nothing beside it. */}
                {(headerActionsSlot || flagSlot) && (
                  <div className={styles.headerActions}>
                    {headerActionsSlot}
                    {flagSlot}
                  </div>
                )}
              </div>
              {canViewFlag && isFlagged && (
                <div className={styles.flagBanner}>
                  <FlagIcon fillColor={FLAG_COLOR} />
                  <pre className={styles.flagBannerText}>{flagComment}</pre>
                  <div className={styles.flagBannerActions}>
                    {flagEditSlot}
                  </div>
                </div>
              )}
            </div>

            {/* Both rows hidden would leave an empty block between two dividers, reading as one
                thick rule. The collector surface is the only caller that hides both. */}
            {(canViewReasons || canViewPriority) && (
              <>
                <div className={styles.divider} />

                <RequestReasonPriority
                  showPriority={canViewPriority}
                  showReasons={canViewReasons}
                  priority={priority}
                  priorityReason={priorityReason}
                  reasonIds={reasonIds}
                  otherReasonText={otherReasonText}
                  reasons={reasonsQuery.data ?? []}
                />
              </>
            )}

            <div className={styles.divider} />

            <div className={styles.participants}>
              <RequestCompanyBlock
                title={t("pickupRequests.form.collectorLabel")}
                company={source}
                participantLinkTo={participantLinkTo}
              />
              <ArrowRight width="24" height="24" />
              <RequestHaulerBlock
                targetCompanyId={targetCompanyId}
                company={target}
                participantLinkTo={participantLinkTo}
              />
              {assignmentActionSlot && (
                <div className={styles.assignmentAction}>
                  {assignmentActionSlot}
                </div>
              )}
            </div>

            <div className={styles.divider} />

            <div className={styles.block}>
              <div className={styles.blockTitle}>
                <span className={styles.blockTitleLabel}>
                  {t("pickupRequests.details.totalToPickup")}
                </span>
                <span>{totalQuantity}</span>
              </div>
              <div className={styles.blockTitle}>
                <span className={styles.blockTitleLabel}>
                  {t("pickupRequests.details.totalWeight")}
                </span>
                <span>{formatWeight(totalWeight)}</span>
              </div>
              {materials.map((material) => (
                <RequestDataRow
                  key={material.code}
                  wideLabel
                  label={material.name || material.code}
                >
                  {material.quantity}
                </RequestDataRow>
              ))}
              {renderDocuments()}
            </div>

            <div className={styles.divider} />

            <div className={styles.block}>
              <span className={styles.blockTitleLabel}>
                {t("pickupRequests.details.pickupDetailsTitle")}
              </span>
              <RequestDataRow label={t("pickupRequests.form.pickupDateLabel")}>
                {getFormattedDateRange(
                  pickupDateFrom ?? "",
                  pickupDateTo ?? ""
                )}
              </RequestDataRow>
              {canViewCarrier && (
                <RequestDataRow label={t("pickupRequests.form.carrierLabel")}>
                  {carrierName || (
                    <span className={styles.rowWarning}>
                      <WarningIcon width="18" height="18" />
                      {t("pickupRequests.grid.awaitingAssignment")}
                    </span>
                  )}
                </RequestDataRow>
              )}
              <RequestDataRow label={t("pickupRequests.form.commentLabel")}>
                {comment || "-"}
              </RequestDataRow>
              {invalidationComment && (
                <RequestDataRow
                  label={t("pickupRequests.details.invalidationReason")}
                >
                  {invalidationComment}
                </RequestDataRow>
              )}
            </div>

            {differsFromLinkedTransaction && (
              <div className={styles.differsBanner}>
                <OutlinedInfoIcon fillColor="var(--blue-dark)" />
                <span>{t("pickupRequests.details.differsFromTransaction")}</span>
              </div>
            )}

            <div className={styles.divider} />
            {actionsSlot && (
              <div className={styles.actions}>{actionsSlot}</div>
            )}

            {navigation && (
              <EntityNavigation
                idPrefix="request-bottom"
                numbers={navigation.numbers}
                total={navigation.total}
                currentNumber={requestNumber}
                buildUrl={(nextNumber) =>
                  `${returnUrl}/${nextNumber}?navigation=true`}
                formatPosition={(position, total) =>
                  t("pickupRequests.navigation.position", { position, total })}
                previousLabel={t("pickupRequests.navigation.previous")}
                nextLabel={t("pickupRequests.navigation.next")}
                onNeedMore={navigation.onNeedMore}
              />
            )}
          </div>
        </main>
        {hasSidebar && (
          <CollapseComponent storageKey="sidebarCollapsed">
            <RecordsSidebar
              objectId={requestNumber}
              entityType="requests"
              entityName={t("requestsDetails.entityName")}
              initiatorEmail={currentUserEmail}
              getDialogTitle={(title) => (
                <DialogHeader
                  title={title}
                  displayName={`${t(
                    "pickupRequests.createDialog.title"
                  )} ${formatRequestNumber(requestNumber)}`}
                />
              )}
              fileNamePrefix={t("requestsDetails.export.fileNamePrefix")}
              fileNameSuffix={requestNumber}
              canViewHistory={canViewHistory}
              canExportHistory={canExportHistory}
              activitiesQueryKey={activitiesQueryKey}
              canViewNotes={canViewNotes}
              canAddNote={canAddNote}
              canExportNotes={false}
              notesQueryKey={["pickupRequestNotes", requestNumber]}
              getNotes={() => getRequestNotes(requestNumber)}
              addNote={(text) => addRequestNote(requestNumber, text)}
            />
          </CollapseComponent>
        )}
      </>
    );
  };

  return (
    <div className={styles.reqDetails}>
      {isLoading && <Loader isBlocking />}
      {!isLoading && !isError && data && renderContent()}
      {isError && (
        <NotFoundPage
          returnLink="/requests"
          title={
            isRequestNotFound
              ? "requestsDetails.error.notFoundTitle"
              : "errors.somethingWrong"
          }
          descriptions={["requestsDetails.error.description"]}
          returnLinkDescription="requestsDetails.error.returnLink"
        />
      )}
    </div>
  );
};

export default PickupRequestDetails;
